import { Component, computed, input, Input, model, OnInit } from '@angular/core';
import {
  AdditionalProductsSummaryPoint,
  Address,
  IAktionen,
  IBusinessData,
  IdCard,
  IERechnungsInfo,
  IProduktSelektion,
  Product,
  SummaryContractInfo,
  TariffSelectionCardData,
} from '@mwe/models';
import { getAddressGroupFormatted, sortProductsByCategory } from '@mwe/utils';
import { AddressComponent } from '../../address/address.component';
import { CollapseComponent } from '../../collapse/collapse.component';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { TariffSelectionContainerComponent } from '../../tariff/tariff-selection-container/tariff-selection-container.component';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { WizardProductCardComponent } from '../../product-card/wizard-product-card/wizard-product-card.component';
import { ProductsSummaryInvoiceOldComponent } from './products-summary-invoice-old.component';
import { TariffCardWaermeComponent } from '../../tariff/products/waerme/tariff-card-waerme.component';
import { ProductSummaryContractInfoComponent } from './product-summary-contract-info.component';
import { InfoboxComponent } from '../../infobox/infobox.component';

@Component({
  selector: 'mwe-products-summary',
  templateUrl: './products-summary.component.html',
  imports: [
    AddressComponent,
    CollapseComponent,
    NgTemplateOutlet,
    TranslateDirective,
    TariffSelectionContainerComponent,
    BasicCardComponent,
    WizardProductCardComponent,
    ProductsSummaryInvoiceOldComponent,
    TariffCardWaermeComponent,
    TranslatePipe,
    ProductSummaryContractInfoComponent,
    InfoboxComponent,
  ],
})
export class ProductsSummaryComponent implements OnInit {
  @Input() collapseId: string;
  @Input() address: Address;
  @Input() invoiceAddress: Address;
  @Input() initialState: 'expanded' | 'collapsed';
  @Input() stromOrGasProducts: Product[];
  @Input() fernwaermeProducts: Product[];
  @Input() registrationAt: string;
  @Input() registerStromGasTitle = 'summary.productsFromWEV';
  @Input() registerStromGasText: string;
  registerStromGasInfoMessage = input<string>();
  @Input() registerFernwaermeTitle = 'summary.checkRegistrationForProducts';
  @Input() registerFernwaermeSubTitle = 'summary.checkRegistrationForProducts';
  @Input() registerFernwaermeText: string;
  @Input() registerFernwaermeTextPlaceholder = new Map<string, string>();
  @Input() birthDate: string;
  @Input() idCard: IdCard;
  @Input() salutation: string;
  @Input() title: string;
  @Input() titlePrefix: string;
  @Input() titleSuffix: string;
  @Input() firstname: string;
  @Input() lastname: string;
  @Input() email: string;
  @Input() eRechnungInfo: IERechnungsInfo;
  @Input() paymentIcon: string;
  @Input() paymentText: string;
  @Input() voucherInfo: IAktionen;
  @Input() showMeterReading = true;
  @Input() invoiceInfoAlertMessage = '';
  @Input() isCollapsable = true;
  @Input() personalBillingTitle = 'summary.personalBilling';
  @Input() areBankAccountInfoTextsVisible = false;
  @Input() contactDataEmailAddress: string;
  @Input() showStromGasTarifDetails = true;
  @Input() additionalBulletPointsStromGas: AdditionalProductsSummaryPoint[];
  @Input() additionalBulletPointsFernwaerme: AdditionalProductsSummaryPoint[];
  @Input() showFernwaermeInvoiceData = false;
  @Input() isPaymentInfoVisible = true;
  @Input() showTariffDetails = true;
  @Input() showTariffDetailsLink = false;
  @Input() productSelection: IProduktSelektion;
  @Input() customerDataType: string;
  @Input() businessData: IBusinessData;
  @Input() grundpreisLabelOverwrite: string;
  @Input() mgmCode: string;
  @Input() isCollapseHeaderVisible = true;
  @Input() isKundeWerden = false;
  @Input() showWaermeTariffClassesAsideOfIcon = false;
  @Input() useNewWaermeCards = false;
  @Input() infoTextCardLabel: string;
  showDeliveryInfosContractInfo = input(false);
  countryCode = input<string>();
  telefonNumber = input<string>();
  selectedTariffs = input<TariffSelectionCardData[]>([]);
  contractInfos = model<SummaryContractInfo[]>(null);
  @Input() isStatusPage = false;

  voucherStrom: string;
  voucherGas: string;
  productCategories: string[];
  formattedAddress: string;
  getFullNameWithTitle = computed<string>(() => {
    let value = '';

    if (this.salutation) {
      value += `${this.salutation} `;
    }
    if (this.titlePrefix) {
      value += `${this.titlePrefix} `;
    }
    if (this.title) {
      value += `${this.title} `;
    }
    value += `${this.firstname} ${this.lastname}`;
    if (this.titleSuffix) {
      value += ` ${this.titleSuffix}`;
    }

    return value;
  });

  ngOnInit(): void {
    this.setupVoucherMessages();
    this.setCategories();
    this.formattedAddress = this.invoiceAddress
      ? getAddressGroupFormatted(this.invoiceAddress, true)
      : getAddressGroupFormatted(this.address, true);
    if (this.stromOrGasProducts) {
      sortProductsByCategory(this.stromOrGasProducts);
    }
  }

  setupVoucherMessages(): void {
    if (this.voucherInfo?.infoTextKey?.length > 0) {
      if (this.voucherInfo.infoTextKey[0].toLowerCase().startsWith('strom')) {
        this.voucherStrom = this.voucherInfo.infoText[0];
        this.voucherGas = this.voucherInfo.infoText[1];
      } else {
        this.voucherStrom = this.voucherInfo.infoText[1];
        this.voucherGas = this.voucherInfo.infoText[0];
      }
    }
  }

  private setCategories(): void {
    let allProducts = [];

    if (Array.isArray(this.stromOrGasProducts)) {
      allProducts = allProducts.concat(this.stromOrGasProducts);
    }

    if (Array.isArray(this.fernwaermeProducts)) {
      allProducts = allProducts.concat(this.fernwaermeProducts);
    }

    this.productCategories = allProducts.map(product => product.category);
  }
}
